import {Injectable} from '@angular/core';
import AudioTrackModel from '../../../models/audio-track.model';
import {BrowserService} from './browser.service';
import {select, Store} from '@ngrx/store';
import VideoState from '../stores/root-store/video.state';
import {UpdateVideoMasterVolumeAction} from '../stores/root-store/actions/video.action';
import {selectVideoScenesData} from '../stores/root-store/selectors/video.selector';
import {take} from 'rxjs/operators';
import {EovAudioContext} from '../interfaces/EovAudioContext';

@Injectable({
    providedIn: 'root'
})
export class AudioService {
    constructor(private browserService: BrowserService,
        private store: Store<{ video: VideoState }>
    ) {}

    shouldUseAudioContext() {
        return this.browserService.isAppleDevice();
    }

    isLoaded(audioTracks: Array<AudioTrackModel>): boolean {
        return audioTracks.filter(audio => audio.isPlaying).every(audio => audio.loaded);
    }

    resetAudioTracks(audioTracks: Array<AudioTrackModel>): void {
        if (!audioTracks || this.shouldUseAudioContext()) {
            return;
        }

        audioTracks.forEach(audio => {
            const audioEl = document.getElementById('audio_' + audio.name) as HTMLAudioElement;
            audioEl.currentTime = 0;
        });
    }

    getAudioContext(): AudioContext {
    // TS bug where window not contains AudioContext see: https://github.com/microsoft/TypeScript/issues/31686
        (window as any).AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;

        const audioContext = {
            context: new AudioContext(),
            unlocked: false
        } as EovAudioContext;

        ['touchstart', 'click', 'touchend'].forEach(
            event => window.addEventListener(event, this.unlockAudioContext.bind(this, audioContext))
        );

        return audioContext.context;
    }

    private async unlockAudioContext(audioContext: EovAudioContext): Promise<void> {
        if (audioContext.unlocked) {
            return;
        }

        // create empty buffer
        const buffer = audioContext.context.createBuffer(1, 1, 44100);
        const source = audioContext.context.createBufferSource();
        source.buffer = buffer;

        // Hack for Android chrome. Audio context need to be resumed after user gesture
        await audioContext.context.resume();

        source.connect(audioContext.context.destination);
        source.start(0);
        source.stop(0);
        source.disconnect();

        audioContext.unlocked = true;
    }
}
